import { useCallback, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import type {
  ContentVersion,
  GenerationDetails,
  VersionActor,
  VersionType,
} from '../types.ts/versioning';

export type { ContentVersion, GenerationDetails, VersionActor, VersionType };

type UseContentVersioningOptions = {
  /** Called when a new version is saved - can be used to persist to backend */
  onVersionSaved?: (versions: ContentVersion[]) => void;
};

type UseContentVersioningReturn = {
  /** All versions, newest first (includes soft-deleted) */
  versions: ContentVersion[];
  /** Active (non-deleted) versions, newest first */
  activeVersions: ContentVersion[];
  /** Current version ID being viewed */
  currentVersionId: string | null;
  /** Current version object */
  currentVersion: ContentVersion | undefined;
  /** Latest (newest) version */
  latestVersion: ContentVersion | undefined;
  /** Index of current version (0 = latest) */
  currentVersionIndex: number;
  /** Whether viewing an older version */
  isViewingPastVersion: boolean;
  /** Version counter for labeling */
  versionCounter: number;
  /** Save a new version (for AI generation/revision) */
  saveVersion: (
    content: string,
    type: VersionType,
    options?: {
      prompt?: string;
      generationDetails?: GenerationDetails;
      username?: string;
      /** Override the auto-generated label (used for 'named' versions). */
      labelOverride?: string;
      /** Pin this version (shows in "Named only" filter). Implied true for type='named'. */
      isNamed?: boolean;
    },
  ) => ContentVersion;
  /** Save a user-pinned "named" version via Save-as-version. Returns null if content is unchanged from the last saved version (dedupe). */
  saveNamedVersion: (
    content: string,
    name: string,
    username?: string,
  ) => ContentVersion | null;
  /** Select a version to view */
  selectVersion: (version: ContentVersion) => void;
  /** Undo to previous version (returns the version content or null) */
  undo: () => string | null;
  /** Redo to next version (returns the version content or null) */
  redo: () => string | null;
  /** Can undo (has previous versions) */
  canUndo: boolean;
  /** Can redo (has newer versions from current position) */
  canRedo: boolean;
  /** Reset versions (e.g., when loading different content) */
  resetVersions: () => void;
  /** Set versions from external source (e.g., loading from storage) */
  setVersions: (versions: ContentVersion[], counter?: number) => void;
  /** Mark that AI generation is starting (prevents user edit version during generation) */
  startAIGeneration: (isRevision: boolean, prompt?: string) => void;
  /** Mark that AI generation completed. If content is provided and a pending AI version exists, creates the version automatically. */
  endAIGeneration: (
    content?: string,
    opts?: {
      prompt?: string;
      generationDetails?: GenerationDetails;
      username?: string;
    },
  ) => ContentVersion | null;
  /** Whether AI generation is in progress */
  isGenerating: boolean;
  /** Rename a version */
  renameVersion: (versionId: string, newLabel: string) => void;
  /** Delete a version */
  deleteVersion: (versionId: string) => void;
};

export function useContentVersioning(
  options: UseContentVersioningOptions = {},
): UseContentVersioningReturn {
  const { onVersionSaved } = options;

  const [versions, setVersions] = useState<ContentVersion[]>([]);
  const [currentVersionId, setCurrentVersionId] = useState<string | null>(null);
  const versionCounterRef = useRef(0);

  // Track AI generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const pendingAIVersionRef = useRef<{
    isRevision: boolean;
    prompt?: string;
  } | null>(null);

  // Derived state — filter out soft-deleted versions for navigation
  const activeVersions = versions.filter(v => !v.deleted);
  const currentVersion = activeVersions.find(v => v.id === currentVersionId);
  const latestVersion = activeVersions[0];
  const currentVersionIndex = activeVersions.findIndex(
    v => v.id === currentVersionId,
  );
  const isViewingPastVersion = currentVersionIndex > 0;

  // Can undo if there's a previous version
  const canUndo =
    currentVersionIndex < activeVersions.length - 1 && currentVersionIndex >= 0;
  // Can redo if we're viewing a past version
  const canRedo = currentVersionIndex > 0;

  const saveVersion = useCallback(
    (
      content: string,
      type: VersionType,
      opts?: {
        prompt?: string;
        generationDetails?: GenerationDetails;
        username?: string;
        labelOverride?: string;
        isNamed?: boolean;
      },
    ): ContentVersion => {
      // Increment version counter
      versionCounterRef.current += 1;
      const versionNumber = versionCounterRef.current;

      // Derive actor from version type
      const actor: VersionActor =
        type === 'user_revision' || type === 'named' ? 'user' : 'ai';

      // Create label: version number separate from source
      let sourceLabel: string;
      switch (type) {
        case 'generated':
          sourceLabel = 'AI Generated';
          break;
        case 'ai_revision':
          sourceLabel = 'AI Revision';
          break;
        case 'user_revision':
          sourceLabel = 'User';
          break;
        case 'named':
          sourceLabel = 'Named';
          break;
      }
      const label = opts?.labelOverride ?? `v${versionNumber} · ${sourceLabel}`;

      const newVersion: ContentVersion = {
        id: uuid(),
        content,
        timestamp: new Date(),
        label,
        versionNumber,
        actor,
        username: opts?.username,
        prompt: opts?.prompt,
        generationDetails: opts?.generationDetails,
        isNamed: opts?.isNamed ?? type === 'named',
      };

      const newVersions = [newVersion, ...versions];
      setVersions(newVersions);
      setCurrentVersionId(newVersion.id);

      // Notify callback
      onVersionSaved?.(newVersions);

      return newVersion;
    },
    [versions, onVersionSaved],
  );

  const saveNamedVersion = useCallback(
    (
      content: string,
      name: string,
      username?: string,
    ): ContentVersion | null => {
      return saveVersion(content, 'named', {
        username,
        labelOverride: name.trim() || undefined,
        isNamed: true,
      });
    },
    [saveVersion],
  );

  const selectVersion = useCallback((version: ContentVersion) => {
    setCurrentVersionId(version.id);
  }, []);

  const undo = useCallback((): string | null => {
    if (!canUndo) return null;

    const prevVersion = activeVersions[currentVersionIndex + 1];
    if (prevVersion) {
      setCurrentVersionId(prevVersion.id);
      return prevVersion.content;
    }
    return null;
  }, [activeVersions, currentVersionIndex, canUndo]);

  const redo = useCallback((): string | null => {
    if (!canRedo) return null;

    const nextVersion = activeVersions[currentVersionIndex - 1];
    if (nextVersion) {
      setCurrentVersionId(nextVersion.id);
      return nextVersion.content;
    }
    return null;
  }, [activeVersions, currentVersionIndex, canRedo]);

  const resetVersions = useCallback(() => {
    setVersions([]);
    setCurrentVersionId(null);
    versionCounterRef.current = 0;
  }, []);

  const setVersionsExternal = useCallback(
    (newVersions: ContentVersion[], counter?: number) => {
      setVersions(newVersions);
      if (newVersions.length > 0) {
        setCurrentVersionId(newVersions[0].id);
      }
      if (counter !== undefined) {
        versionCounterRef.current = counter;
      } else if (newVersions.length > 0) {
        // Use the highest versionNumber to avoid collisions with new versions
        const maxVersion = Math.max(
          ...newVersions.map(v => v.versionNumber ?? 0),
          newVersions.length,
        );
        versionCounterRef.current = maxVersion;
      }
    },
    [],
  );

  const startAIGeneration = useCallback(
    (isRevision: boolean, prompt?: string) => {
      setIsGenerating(true);
      pendingAIVersionRef.current = { isRevision, prompt };
    },
    [],
  );

  const endAIGeneration = useCallback(
    (
      content?: string,
      opts?: {
        prompt?: string;
        generationDetails?: GenerationDetails;
        username?: string;
      },
    ): ContentVersion | null => {
      setIsGenerating(false);

      if (content && pendingAIVersionRef.current) {
        const type: VersionType = pendingAIVersionRef.current.isRevision
          ? 'ai_revision'
          : 'generated';
        const prompt = opts?.prompt || pendingAIVersionRef.current.prompt;
        pendingAIVersionRef.current = null;
        return saveVersion(content, type, { ...opts, prompt });
      }

      pendingAIVersionRef.current = null;
      return null;
    },
    [saveVersion],
  );

  const renameVersion = useCallback(
    (versionId: string, newLabel: string) => {
      const updatedVersions = versions.map(v =>
        v.id === versionId ? { ...v, label: newLabel } : v,
      );
      setVersions(updatedVersions);

      // Notify callback to persist changes
      onVersionSaved?.(updatedVersions);
    },
    [versions, onVersionSaved],
  );

  const deleteVersion = useCallback(
    (versionId: string) => {
      // Soft-delete: mark as deleted but keep in array so it persists to storage.
      // This allows recovery and keeps version numbering stable.
      const updatedVersions = versions.map(v =>
        v.id === versionId ? { ...v, deleted: true } : v,
      );
      setVersions(updatedVersions);

      // If the deleted version was the current one, select the nearest non-deleted version
      if (currentVersionId === versionId) {
        const activeVersions = updatedVersions.filter(v => !v.deleted);
        if (activeVersions.length > 0) {
          setCurrentVersionId(activeVersions[0].id);
        } else {
          setCurrentVersionId(null);
        }
      }

      // Notify callback to persist changes
      onVersionSaved?.(updatedVersions);
    },
    [versions, currentVersionId, onVersionSaved],
  );

  return {
    versions,
    activeVersions,
    currentVersionId,
    currentVersion,
    latestVersion,
    currentVersionIndex,
    isViewingPastVersion,
    versionCounter: versionCounterRef.current,
    saveVersion,
    saveNamedVersion,
    selectVersion,
    undo,
    redo,
    canUndo,
    canRedo,
    resetVersions,
    setVersions: setVersionsExternal,
    startAIGeneration,
    endAIGeneration,
    isGenerating,
    renameVersion,
    deleteVersion,
  };
}
